import util from '../util';
import Matter from 'matter-js';
import decomp from 'poly-decomp';
import Widget from './Widget';

Matter.Common.setDecomp(decomp);

const MAX_SIZE = 200;
const PADDING = 100;
const CANVAS_SIZE = MAX_SIZE + PADDING * 2;
const N = 24;
// Above this many physics bodies, stop searching for a non-overlapping spawn spot (the search is
// O(bodies) per attempt): the wall is full, there's no free space, and the solver separates the
// overlap within a frame or two. Prevents a spawn burst from becoming O(bodies²).
const PLACEMENT_SEARCH_MAX_BODIES = 80;
const epsilon = 1e-4;
const directions = Array.from({length: N}, (_, i) => {
	const angle = 2 * Math.PI * i / N;
	return [Math.cos(angle), Math.sin(angle)];
});

function computeCentroid(vertices) {
	let area = 0, cx = 0, cy = 0;
	const count = vertices.length;
	for(let i = 0; i < count; i++) {
		const p1 = vertices[i];
		const p2 = vertices[(i + 1) % count];
		const cross = p1.x * p2.y - p2.x * p1.y;
		area += cross;
		cx += (p1.x + p2.x) * cross;
		cy += (p1.y + p2.y) * cross;
	}
	area *= 0.5;
	cx /= (6 * area);
	cy /= (6 * area);
	return { x: cx, y: cy };
}

export default class EmoteWallWidget extends Widget {
	constructor({ container, baseUrl }) {
		super({ container });
		this.baseUrl = baseUrl || '';

		this.engine = null;
		this.world = null;
		this.ctx = null;
		this.hullCache = {};
		// imgId-sz keys whose hull couldn't be turned into a body — skip straight to the
		// circle fallback next time instead of recomputing and failing again.
		this.hullFailCache = new Set();
		this._animFrameId = null;
		this._lastTime = performance.now();
		this._runner = null;
		this._debugCanvas = null;
		this._debugCtx = null;
		this._bounds = [];
		this._resizeObserver = null;
		// Live list of on-screen emote elements, oldest first. Tracked explicitly so spawn/evict
		// don't have to run querySelectorAll('.bounce') (an O(n) DOM walk) on every single emote.
		this._active = [];
	}

	// True when `url` resolves to a different origin than the page (so it needs the CORS proxy
	// to be canvas-readable). Relative/blob/data URLs and anything unparseable are same-origin.
	_isCrossOrigin(url) {
		try {
			return new URL(url, window.location.href).origin !== window.location.origin;
		} catch(e) {
			return false;
		}
	}


	async init(initialData = {}) {
		this._startAnimLoop();
		await super.init(initialData);
	}

	applySettings(settings) {
		const prev = this.settings;
		// Also restart on a debug-flag flip, not just a physics on/off flip — the debug
		// canvas is only ever created inside _initPhysics(), which early-returns once
		// the engine already exists, so toggling "Show shapes" while physics is already
		// running would otherwise need a physics off/on cycle to actually take effect.
		if(prev && prev !== settings && (!!prev.physics !== !!settings.physics || !!prev.debug !== !!settings.debug)) {
			this._destroyPhysics();
			for(const el of Array.from(this.container.querySelectorAll('.bounce'))) el.remove();
			this._active = [];
		}
		super.applySettings(settings);
		if(settings.physics)
			this._initPhysics();
		else if(this.world)
			this.world.gravity.y = parseFloat(settings.gravity || 0) || 0;
		if(this.world && prev && (
			prev.emoteSize !== settings.emoteSize ||
			prev.stickerSize !== settings.stickerSize
		)) {
			this._rescaleObjects(settings);
		}
	}

	_destroyPhysics() {
		if(this._resizeObserver) {
			this._resizeObserver.disconnect();
			this._resizeObserver = null;
		}
		if(this.engine) {
			if(this._runner) {
				Matter.Runner.stop(this._runner);
				this._runner = null;
			}
			if(this._debugCanvas) {
				this._debugCanvas.remove();
				this._debugCanvas = null;
				this._debugCtx = null;
			}
			Matter.Engine.clear(this.engine);
			this.engine = null;
			this.world = null;
			this._bounds = [];
		}
		this.container.querySelector('canvas')?.remove();
		this.ctx = null;
	}


	destroy() {
		if(this._animFrameId != null) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
		this._destroyPhysics();
		document.getElementById(`emoteWallStyle-${this.container.id}`)?.remove();
		super.destroy();
	}

	_generateCSS() {
		document.getElementById(`emoteWallStyle-${this.container.id}`)?.remove();

		const s = this.settings;
		const id = this.container.id;
		const css = `
			:root {
				--animate-duration: 200ms;
				--emote-size: ${(s.emoteSize || 5) * 10}px;
				--sticker-size: ${(s.stickerSize || 5) * 10}px;
			}
			#${id} .bounce {
				position: absolute;
				will-change: transform;
				pointer-events: none;
				opacity: 0;
				transition: opacity 200ms;
			}
			#${id} .emote {
				width: var(--emote-size);
				height: var(--emote-size);
				object-fit: contain;
				text-align: center;
			}
			#${id} .sticker {
				width: var(--sticker-size);
				height: var(--sticker-size);
				object-fit: contain;
			}
			#${id} .emoji {
				font-size: var(--emote-size);
				line-height: var(--emote-size);
			}
			#${id} canvas {
				z-index: 50;
				position: absolute;
			}
			#${id} img, #${id} span {
				z-index: 100;
			}
		`;

		const style = document.createElement('style');
		style.type = 'text/css';
		style.id = `emoteWallStyle-${id}`;
		style.appendChild(document.createTextNode(css));
		(document.head || document.getElementsByTagName('head')[0]).appendChild(style);
	}

	_rescaleObjects(settings) {
		for(const elem of this.container.querySelectorAll('.bounce')) {
			if(!elem.matterBody || !elem.physicsSize) continue;
			const newSize = elem.classList.contains('sticker')
				? (settings.stickerSize || 5) * 10
				: (settings.emoteSize || 5) * 10;
			if(newSize === elem.physicsSize) continue;
			const scale = newSize / elem.physicsSize;
			Matter.Body.scale(elem.matterBody, scale, scale);
			if(elem.ox != null) elem.ox *= scale;
			if(elem.oy != null) elem.oy *= scale;
			elem.physicsSize = newSize;
		}
	}

	// Debug hull overlay: enabled by settings.debug (the "Show shapes" checkbox on the
	// widget settings page) or the undocumented ?debug=1 URL param (the latter isn't a
	// real setting, so it bypasses the normal settings UI).
	_isDebug() {
		return !!(this.settings && this.settings.debug) || this._isUrlDebug();
	}

	// The raw URL param additionally reveals the hull-computation scratch canvas (see
	// _initPhysics) — a pixel-level aid for inspecting why a hull comes out wrong, not
	// something "Show shapes" should turn on: it's unclipped and un-pointer-events'd, so
	// on the settings page it would sit on top of — and block clicks on — the preview's
	// own controls underneath.
	_isUrlDebug() {
		return this._urlParams.debug === '1' || this._urlParams.debug === 'true';
	}

	_initPhysics() {
		if(this.engine) {
			this.world.gravity.y = parseFloat(this.settings.gravity || 0) || 0;
			return;
		}

		this.engine = Matter.Engine.create();
		this.world = this.engine.world;

		if(this._isDebug()) {
			// Transparent overlay canvas on top of the emotes, onto which _drawDebug()
			// paints each body's collision hull so it can be checked against the artwork.
			this._debugCanvas = document.createElement('canvas');
			this._debugCanvas.width = this.container.clientWidth;
			this._debugCanvas.height = this.container.clientHeight;
			this._debugCanvas.style.cssText = 'position:absolute;top:0;left:0;z-index:200;pointer-events:none';
			this.container.appendChild(this._debugCanvas);
			this._debugCtx = this._debugCanvas.getContext('2d');
		}

		this._runner = Matter.Runner.create();
		Matter.Runner.run(this._runner, this.engine);

		this._updateBounds(this.container.clientWidth, this.container.clientHeight);

		this._resizeObserver = new ResizeObserver(entries => {
			const { width, height } = entries[0].contentRect;
			this._updateBounds(width, height);
			if(this._debugCanvas) {
				this._debugCanvas.width = width;
				this._debugCanvas.height = height;
			}
		});
		this._resizeObserver.observe(this.container);

		if(this.settings.shapes) {
			const shapes = this.settings.shapes.split('\n')
				.map(s => s.split(' ').filter(a => a).map(a => a.split(','))
				.filter(a => a.length === 2).map(a => ({ x: parseFloat(a[0]), y: parseFloat(a[1]) })));
			for(const shape of shapes) {
				if(shape.length < 3) continue;
				const centroid = computeCentroid(shape);
				Matter.Composite.add(this.world, Matter.Bodies.fromVertices(centroid.x, centroid.y, shape, { isStatic: true }));
			}
		}

		const canvas = document.createElement('canvas');
		canvas.width = CANVAS_SIZE;
		canvas.height = CANVAS_SIZE;
		// The hull-computation canvas is an internal scratch buffer holding the last
		// emote's alpha mask (and, in debug, the hull drawn over it). Hidden normally;
		// shown in the corner under ?debug=1 to inspect why a hull comes out wrong.
		if(this._isUrlDebug())
			canvas.style.cssText = 'position:absolute;top:0;left:0;z-index:300;outline:2px solid #ff00ff;background:rgba(0,0,0,.35)';
		else
			canvas.style.visibility = 'hidden';
		this.container.appendChild(canvas);
		this.ctx = canvas.getContext('2d', { willReadFrequently: true, desynchronized: true });

		this.world.gravity.y = parseFloat(this.settings.gravity || 0) || 0;
	}

	_updateBounds(width, height) {
		if(!this.world) return;
		for(const b of this._bounds) Matter.Composite.remove(this.world, b);
		const t = 300;
		this._bounds = [
			Matter.Bodies.rectangle(width / 2,      -t / 2,          width + t * 2, t, { isStatic: true }), // top
			Matter.Bodies.rectangle(width / 2,      height + t / 2,  width + t * 2, t, { isStatic: true }), // bottom
			Matter.Bodies.rectangle(-t / 2,         height / 2,      t, height + t * 2, { isStatic: true }), // left
			Matter.Bodies.rectangle(width + t / 2,  height / 2,      t, height + t * 2, { isStatic: true }), // right
		];
		Matter.Composite.add(this.world, this._bounds);
	}

	_getImageHull(img, sz, imgId) {
		const cacheId = `${imgId}-${sz}`;
		if(this.hullCache[cacheId]) return this.hullCache[cacheId];

		let scale = Math.min(MAX_SIZE / img.naturalWidth, MAX_SIZE / img.naturalHeight);
		let ew = Math.floor(img.naturalWidth * scale);
		let eh = Math.floor(img.naturalHeight * scale);
		let ex = PADDING + (MAX_SIZE - ew) / 2;
		let ey = PADDING + (MAX_SIZE - eh) / 2;

		this.ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
		if(img.tagName === 'IMG') {
			this.ctx.drawImage(img, ex, ey, ew, eh);
		} else if(img.tagName === 'SPAN') {
			this.ctx.font = `${MAX_SIZE}px Arial`;
			this.ctx.fillStyle = 'black';
			this.ctx.textAlign = 'left';
			this.ctx.textBaseline = 'ideographic';
			this.ctx.fillText(img.innerText, PADDING, PADDING + MAX_SIZE);
			ex = 0; ey = 0; ew = CANVAS_SIZE; eh = CANVAS_SIZE;
		}

		const { data } = this.ctx.getImageData(ex, ey, ew, eh);
		const dists = new Array(N).fill(Infinity);
		let idx = 3;
		for(let y = ey; y < ey + eh; y++) {
			let minX = CANVAS_SIZE + 1;
			let maxX = -1;
			for(let x = ex; x < ex + ew; x++) {
				const alpha = data[idx];
				idx += 4;
				if(alpha < 128) continue;
				if(x < minX) minX = x;
				if(x > maxX) maxX = x;
			}
			if(minX > maxX) continue;
			for(let i = 0; i < N; i++) {
				const [dx, dy] = directions[i];
				const ydy = (y - PADDING) * dy;
				const proj = (minX - PADDING) * dx + ydy;
				if(proj < dists[i]) dists[i] = proj;
				const proj2 = (maxX - PADDING) * dx + ydy;
				if(proj2 < dists[i]) dists[i] = proj2;
			}
		}

		const points = [];
		for(let i = 0; i < N; i++) {
			const j = (i + 1) % N;
			const [nx1, ny1] = directions[i];
			const [nx2, ny2] = directions[j];
			const d1 = dists[i];
			const d2 = dists[j];
			if(d1 === Infinity || d2 === Infinity) continue;

			const det = nx1 * ny2 - ny1 * nx2;
			if(Math.abs(det) < 1e-6) continue;

			let x = (d1 * ny2 - d2 * ny1) / det;
			let y = (nx1 * d2 - nx2 * d1) / det;

			const last = points[points.length - 1];
			if(last && (x - last.x) ** 2 + (y - last.y) ** 2 < epsilon ** 2) continue;
			if(i === N - 1 && points.length > 0) {
				const first = points[0];
				if((x - first.x) ** 2 + (y - first.y) ** 2 < epsilon ** 2) continue;
			}

			x *= sz / MAX_SIZE;
			y *= sz / MAX_SIZE;
			points.push({ x, y });
		}

		// Redundant support-line directions emit (near-)coincident intersection points —
		// degenerate near-zero-length edges — which make Matter read this otherwise-convex
		// hull as concave and run polygon decomposition (which sometimes fails to build a
		// body). Dropping the coincident vertices restores a clean convex polygon, so a full
		// Vertices.hull() pass isn't needed.
		const hull = [];
		const mergeSq = (sz * 0.02) ** 2; // merge vertices closer than ~2% of the emote size
		for(const p of points) {
			const last = hull[hull.length - 1];
			if(last && (p.x - last.x) ** 2 + (p.y - last.y) ** 2 < mergeSq) continue;
			hull.push(p);
		}
		if(hull.length > 2) {
			const a = hull[0], b = hull[hull.length - 1];
			if((a.x - b.x) ** 2 + (a.y - b.y) ** 2 < mergeSq) hull.pop();
		}

		if(this._isDebug() && hull.length) {
			// Draw the FINAL (deduped) hull over the alpha mask — numbered, and mapped back
			// into canvas space — so it matches the body actually used (no stale raw points).
			const k = MAX_SIZE / sz;
			this.ctx.lineWidth = 2;
			this.ctx.strokeStyle = '#ff00ff';
			this.ctx.fillStyle = '#ffff00';
			this.ctx.font = '12px monospace';
			this.ctx.beginPath();
			this.ctx.moveTo(hull[0].x * k + PADDING, hull[0].y * k + PADDING);
			for(let i = 1; i < hull.length; i++) this.ctx.lineTo(hull[i].x * k + PADDING, hull[i].y * k + PADDING);
			this.ctx.closePath();
			this.ctx.stroke();
			hull.forEach((p, i) => this.ctx.fillText(String(i), p.x * k + PADDING, p.y * k + PADDING));
			this.ctx.fillStyle = '#00ffff';
			this.ctx.font = 'bold 16px monospace';
			this.ctx.fillText(`${hull.length} verts`, 6, 18);
		}

		if(hull.length > 0) this.hullCache[cacheId] = hull;
		return hull;
	}

	_extractEmotes(msg) {
		const emotes = [];
		if(this.settings.showGifts) {
			if(msg.giftIcon) {
				for(let i = 0; i < (msg.multiplier || 1); i++)
					emotes.push({ type: 'sticker', src: msg.giftIcon, proxy: msg.giftIconProxy, attrs: msg.giftIconAttributes });
			} else if(this.giftTypes?.[msg.serviceId]?.[msg?.giftType]) {
				const donoSettings = this.giftTypes[msg.serviceId][msg.giftType];
				let images;
				if(donoSettings.images?.length > 0) images = donoSettings.images;
				else if(donoSettings.image) images = [donoSettings.image];
				if(images?.length > 0) {
					for(let i = 0; i < (msg.multiplier || 1); i++)
						emotes.push({ type: 'sticker', src: util.randomItem(images.map(i => i.url)) });
				}
			}
		}
		for(const span of msg.spans) {
			if(typeof span === 'string') {
				if(this.settings.showEmojis)
					for(const emoji of util.extractEmojis(span))
						emotes.push({ emoji, type: 'emoji' });
			} else if(span.type === 'text') {
				if(this.settings.showEmojis)
					for(const emoji of util.extractEmojis(span.text))
						emotes.push({ emoji, type: 'emoji' });
			} else if(span.type === 'emote') {
				// "Member emotes only" means this channel's own membership emotes
				// specifically — not just any custom emote, and not another channel's
				// members-only emote either (some platforms let a chat show those). A bot
				// sets isChannel/isMembers per emote as it parses the message, the same
				// facts user_channel_emotes.is_channel/is_members record for the catalog.
				if(!this.settings.memberEmotesOnly || (span.isChannel && span.isMembers))
					emotes.push({ src: span.src, type: 'emote', proxy: span.proxy, attrs: span.attrs });
			} else if(span.type === 'sticker') {
				if(this.settings.showGifts)
					emotes.push({ src: span.src, type: 'sticker', proxy: span.proxy, attrs: span.attrs });
			}
		}
		return emotes;
	}

	_evictOldest() {
		this._removeEmoteEl(this._active[0]);
	}

	// Single removal path for an emote element — keeps `_active`, the Matter body and the DOM
	// node in sync no matter what triggers the removal (eviction, duration expiry, NaN cleanup).
	// Idempotent: calling it twice for the same element is a no-op the second time.
	_removeEmoteEl(el) {
		if(!el) return;
		const i = this._active.indexOf(el);
		if(i >= 0) this._active.splice(i, 1);
		if(el.matterBody) {
			Matter.Composite.remove(this.world, el.matterBody);
			el.matterBody = null;
		}
		el.remove();
	}

	async spawnEmotes(emotes) {
		// Fall back to the viewport when the container reports no size (e.g. an OBS
		// source that isn't currently being rendered, or before first layout) —
		// otherwise clientWidth/clientHeight are 0 and every emote spawns at (0, 0),
		// i.e. bunched in the top-left corner instead of spread across the page.
		const width = this.container.clientWidth || this.container.offsetWidth || window.innerWidth;
		const height = this.container.clientHeight || this.container.offsetHeight || window.innerHeight;
		// If we still can't determine a real size, skip this batch rather than spawn
		// everything at (0, 0) in the top-left corner (nothing is visible anyway).
		if(!width || !height) return;
		const limit = this.settings.objectLimit > 0 ? this.settings.objectLimit : Infinity;

		// Load every image up front, in parallel. Previously each emote was awaited one-by-one
		// inside the loop, so a burst (e.g. /spam) became N sequential image loads that stalled
		// the whole batch; Promise.all lets the browser fetch/decode them concurrently, and the
		// physics/DOM work below then runs synchronously with no awaits in between.
		const prepared = await Promise.all(emotes.map(async emote => {
			if(!this.settings.physics || emote.type === 'emoji') return { emote };
			// Only route cross-origin images through the server CORS proxy (needed so the canvas
			// can read pixels for the hull). Same-origin images — e.g. the app's own static emotes
			// — load directly: proxying them is pointless and breaks under Docker, where the
			// container can't reach the localhost URL baked into the src.
			const needsPixels = this.settings.physicsHulls && this._isCrossOrigin(emote.src);
			const proxied = emote.proxy || needsPixels;
			const src = proxied
				? `${this.baseUrl}/proxy/${encodeURIComponent(emote.src)}`
				: emote.src;
			// Hull building reads the image back off a canvas, so it has to be loaded with CORS —
			// which is why it goes through the proxy above. Everything else is only ever displayed,
			// so a host without CORS headers can load plainly.
			const crossOrigin = proxied ? 'anonymous' : util.getAttrValue(emote.attrs, 'crossorigin');
			try {
				return { emote, img: await util.preloadImage(src, { crossOrigin }) };
			} catch(e) {
				// A single failed image (often the CORS proxy) must not abort the batch — drop it.
				return null;
			}
		}));

		for(const item of prepared) {
			if(!item) continue;
			const emote = item.emote;
			while(this._active.length >= limit) this._evictOldest();
			// Once the wall is busy, skip the non-overlapping-spot search below (O(bodies) per try).
			const crowded = !!(this.world && this.world.bodies.length > PLACEMENT_SEARCH_MAX_BODIES);
			if(this.settings.physics) {
				let img = item.img, imgId;
				if(emote.type === 'emoji') {
					img = document.createElement('span');
					img.innerText = imgId = emote.emoji;
				} else {
					// Key the hull cache by the image URL so each emote gets its own hull.
					imgId = emote.src;
				}
				if(!img) continue;

				img.className = `bounce ${emote.type}`;
				img.style.transformOrigin = 'top left';
				this.container.appendChild(img);
				this._active.push(img);
				requestAnimationFrame(() => requestAnimationFrame(() => { img.style.opacity = '1'; }));

				const imgSize = emote.type === 'sticker'
					? (this.settings.stickerSize || 5) * 10
					: (this.settings.emoteSize || 5) * 10;
				// .emote/.sticker are sized to a fixed imgSize×imgSize box in CSS, so use imgSize
				// directly rather than reading offsetWidth/Height (which forces a synchronous reflow
				// per emote). Emoji spans have glyph-dependent widths, so those still measure.
				const scaledWidth = emote.type === 'emoji' ? img.offsetWidth : imgSize;
				const scaledHeight = emote.type === 'emoji' ? img.offsetHeight : imgSize;

				let body;
				const hullKey = `${imgId}-${imgSize}`;
				try {
					if(!this.settings.physicsHulls) throw 'Hull computation disabled';
					if(this.hullFailCache.has(hullKey)) throw 'known un-buildable hull';
					const hull = this._getImageHull(img, imgSize, imgId);
					if(hull.length < 3) throw 'Invalid hull';
					const centroid = computeCentroid(hull);
					// A degenerate (zero-area) hull produces a NaN centroid, which would make
					// the render transform NaN and pin the emote to (0,0) in the top-left
					// corner. Treat it as "no precise shape" and fall back to the circle.
					if(!Number.isFinite(centroid.x) || !Number.isFinite(centroid.y)) throw 'Degenerate hull';
					img.ox = centroid.x;
					img.oy = centroid.y;
					for(let n = 0; n < 5; n++) {
						const sx = Math.random() * (width - scaledWidth) + scaledWidth / 2;
						const sy = Math.random() * (height - scaledHeight) + scaledHeight / 2;
						body = Matter.Bodies.fromVertices(sx, sy, hull, {
							restitution: 0.9,
							friction: 0.05,
							frictionAir: 0.00,
							angle: Math.random() * Math.PI,
						});
						if(this._isDebug() && n === 0) console.log('[hull]', imgId, 'pts:', hull.length, 'convex:', Matter.Vertices.isConvex(hull), 'area:', Math.round(Matter.Vertices.area(hull)), 'ok:', !!body);
						if(!body) {
							// Couldn't build a body from this hull; remember it so future spawns of
							// the same emote skip straight to the circle fallback.
							this.hullFailCache.add(hullKey);
							throw new Error('Could not create body from vertices');
						}
						// Bodies.fromVertices doesn't reliably honor the (x, y) argument for hull
						// bodies — it can leave the body at the vertices' local centroid (~top-left).
						// Force the intended spawn position explicitly.
						Matter.Body.setPosition(body, { x: sx, y: sy });
						if(crowded || Matter.Query.collides(body, this.world.bodies).length === 0) break;
					}
				} catch(e) {
					// Every throw here (hulls disabled, invalid/degenerate hull, un-buildable
					// vertices) is an expected reason to use the circle fallback — not an error.
					// Surface it only in debug mode to keep the console clean in production.
					if(this._isDebug()) console.debug('emote-wall: using circle fallback —', e);
					img.ox = scaledWidth / 2;
					img.oy = scaledHeight / 2;
					for(let n = 0; n < 5; n++) {
						const sx = Math.floor(Math.random() * (width - scaledWidth) + scaledWidth / 2);
						const sy = Math.floor(Math.random() * (height - scaledHeight) + scaledHeight / 2);
						const scale = Math.min(imgSize / scaledWidth, imgSize / scaledHeight);
						// No precise shape: approximate with a circle. Circle-vs-circle collision uses
						// Matter's fast radius check (cheaper than rectangle SAT) and suits round emotes.
						// Radius = half the average of the (scaled) width and height, so the
						// collision circle matches the emote's footprint rather than overshooting it.
						const radius = (scaledWidth * scale + scaledHeight * scale) / 4;
						body = Matter.Bodies.circle(sx, sy, radius, {
							restitution: 0.9, friction: 0.05, frictionAir: 0.00,
							angle: Math.random() * Math.PI,
						});
						if(!body) throw new Error('Could not create body from vertices');
						if(crowded || Matter.Query.collides(body, this.world.bodies).length === 0) break;
					}
				}

				Matter.Body.setVelocity(body, {
					x: (Math.random() - 0.5) * 20,
					y: (Math.random() - 0.5) * 20,
				});
				Matter.Composite.add(this.world, body);
				img.matterBody = body;
				img.physicsSize = imgSize;

				setTimeout(() => {
					img.style.opacity = '0';
					img.addEventListener('transitionend', () => this._removeEmoteEl(img));
				}, this.settings.duration * 1000 - 200);
			} else {
				let img;
				if(emote.type === 'emoji') {
					img = document.createElement('span');
					img.innerText = emote.emoji;
				} else {
					img = document.createElement('img');
					img.src = emote.src;
				}
				img.className = `bounce ${emote.type}`;
				this.container.appendChild(img);
				this._active.push(img);
				requestAnimationFrame(() => requestAnimationFrame(() => { img.style.opacity = '1'; }));

				// Randomize each emote's start position independently so a message with
				// several emotes spreads across the page instead of spawning as one clump.
				img.style.left = `${Math.round(Math.random() * width)}px`;
				img.style.top = `${Math.round(Math.random() * height)}px`;
				img.dx = (Math.random() - 0.5) * 300;
				img.dy = (Math.random() - 0.5) * 300;

				setTimeout(() => {
					img.style.opacity = '0';
					img.addEventListener('transitionend', () => this._removeEmoteEl(img));
				}, this.settings.duration * 1000 - 200);
			}
		}
	}

	_startAnimLoop() {
		this._lastTime = performance.now();
		const animate = (now) => {
			this._tick((now - this._lastTime) / 1000);
			this._lastTime = now;
			this._animFrameId = requestAnimationFrame(animate);
		};
		this._animFrameId = requestAnimationFrame(animate);
	}

	_tick(dt) {
		if(!this.settings) return;
		for(const elem of this.container.childNodes) {
			if(this.settings.physics) {
				if(elem.tagName == 'CANVAS') continue;
				if(!elem.matterBody) continue;
				const { x, y } = elem.matterBody.position;
				const angle = elem.matterBody.angle;
				// A body whose physics state has gone non-finite would render a NaN transform
				// (the browser drops it, pinning the emote to the top-left corner), and its NaN
				// propagates through Matter's solver into neighbouring bodies. Drop it instead.
				if(!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(angle) || !Number.isFinite(elem.ox) || !Number.isFinite(elem.oy)) {
					// Pull the broken body out of the simulation and hide the element; the
					// spawn's duration timeout still removes the DOM node later.
					Matter.Composite.remove(this.world, elem.matterBody);
					elem.matterBody = null;
					elem.style.opacity = '0';
					continue;
				}
				elem.style.transform = `
					translate(${x}px, ${y}px)
					rotate(${angle}rad)
					translate(${-elem.ox}px, ${-elem.oy}px)
				`;
			} else {
				if(!('dx' in elem)) continue;
				let x = elem.offsetLeft;
				let y = elem.offsetTop;
				x += dt * elem.dx;
				y += dt * elem.dy;

				const w = this.container.clientWidth;
				const h = this.container.clientHeight;

				if(x < 0 || x > w - elem.offsetWidth) {
					elem.dx *= -1;
					x = Math.max(0, Math.min(x, w - elem.offsetWidth));
				}
				if(y < 0 || y > h - elem.offsetHeight) {
					elem.dy *= -1;
					y = Math.max(0, Math.min(y, h - elem.offsetHeight));
				}

				elem.style.left = `${x}px`;
				elem.style.top = `${y}px`;
			}
		}
		if(this._debugCtx) this._drawDebug();
	}

	// Paint every physics body's collision hull onto the debug overlay so it can be
	// compared against the emote artwork (undocumented ?debug=1).
	_drawDebug() {
		const ctx = this._debugCtx;
		ctx.clearRect(0, 0, this._debugCanvas.width, this._debugCanvas.height);
		ctx.lineWidth = 2;
		ctx.strokeStyle = '#00e5ff';
		ctx.fillStyle = '#ff2d55';
		for(const body of Matter.Composite.allBodies(this.world)) {
			const parts = body.parts.length > 1 ? body.parts.slice(1) : body.parts;
			for(const part of parts) {
				const v = part.vertices;
				if(!v || !v.length) continue;
				ctx.beginPath();
				ctx.moveTo(v[0].x, v[0].y);
				for(let i = 1; i < v.length; i++) ctx.lineTo(v[i].x, v[i].y);
				ctx.closePath();
				ctx.stroke();
			}
			ctx.beginPath();
			ctx.arc(body.position.x, body.position.y, 2, 0, Math.PI * 2);
			ctx.fill();
		}
	}
}
